import React from "react";
import { FilterX } from "lucide-react";
import { Button } from "../../../ui/button";
import { Icon } from "../../../Shared/Icon";
import { Loader2 } from "lucide-react";
import { RadixSelectControl } from "../../../ui/radix-select";
import { RadixFilterMultiSelect } from "../../../ui/radix-multi-select";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CustomDataGrid from "../../../Shared/Table/CustomDataGrid";
import { thresholdCrossingColumns } from "../../../Shared/Table/Columns";
import { LteKpiList, NrKpiList, API, DIALOGOPENFOR } from "../../../../globals/globals";
import { selectedDeviceSelector } from "../../../../store/api/devices/selectors";
import { useExportThresholdAlertsMutation, useGetAlarmsByDeviceKeyMutation, useGetDrawerCountsMutation, useGetThresholdAlertsQuery } from "../../../../store/api/devices/deviceApiSlice";
import { useGetDeviceFullDetailMutation } from "../../../../store/api/deviceConfig/deviceConfigApiSlice";
import { confirmDialogSelector, paginationModelSelector, selectedNavListItemSelector } from "../../../../store/helpers/helperSelectors";
import { initialState } from "../../../../store/helpers/helperInitialState";
import { searchKeywordSelector } from "../../../../store/search/selector";
import { resetKeyword } from "../../../../store/search/reducer";
import SearchBox from "../../../Shared/SearchBox/SearchBox";
import { useClearKpiAlertThreshold } from "../../../Configuration/Performance/ThresholdCrossingHistory/useClearKpiAlertThreshold";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { GridEventListener, GridRowParams, GridSortModel } from "@mui/x-data-grid";
import { setDrawerCounts, setOpenDeviceDrawer, setSelectedDevice, setSelectedDeviceAlarmCount, setSelectedDeviceAlarms } from "../../../../store/api/devices/reducers";
import { setConfirmDialog, setIsLoadingStatus, setOpenToast } from "../../../../store/helpers/helperReducers";
import { errorMessage, exportInCSV } from "../../../../utils/helpers";
import { selectProfilePermissionsSelector } from "../../../../store/api/auth/selectors";
import ProfilePermissions from "../../../../utils/ProfilePermissions";
import DeviceDrawer from "../../../Drawers/DeviceDrawer";
import { currentSiteGroupsSelector, selectedGeoRegionIdsSelector } from "../../../../store/api/admin/selectors";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import { useRegionDisplay } from "../../../../pages/Administrations/AccessControls/Regions/hooks";
import { devicesSelector } from "../../../../store/filters/Nodes/selectors";
import { setRegionFilterIds, setSelectedDeviceSiteGroup } from "../../../../store/filters/Nodes/reducer";
import { setSelectedNavListItems } from "../../../../store/helpers/helperReducers";
import { setSelectedGeoRegion } from "../../../../store/api/admin/reducers";
import { initialGeoRegion } from "../../../../components/helpers";
import ActiveFilterChips from '../../../Shared/Filter/ActiveFilterChips';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../../ui/tooltip';
import { Input } from "../../../ui/input";

interface ThresholdCrossingProps {
  deviceId?: string;
}

const ThresholdCrossing: React.FC<ThresholdCrossingProps> = ({ deviceId }) => {
  const selectedDevice = useSelector(selectedDeviceSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const canManage = !!permissions?.hasOpCodeGroup("performance_management");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locationPath = location?.pathname.split("/");
  const effectiveDeviceId = deviceId || selectedDevice?.objectKey || locationPath[2];
  const isDeviceView = Boolean(deviceId);

  const [selectedKpi, setSelectedKpi] = React.useState<string>("");
  const [selectedCell, setSelectedCell] = React.useState<string>("");
  const [selectedState, setSelectedState] = React.useState<string>("ALL");
  const [selectedTimeRange, setSelectedTimeRange] = React.useState<string>("");
  const [rowSelectionModel, setRowSelectionModel] = React.useState<any[]>([]);
  const [networkType, setNetworkType] = React.useState<"LTE" | "NR">("LTE");
  const [rowClicked, setRowClicked] = React.useState(false);
  const [pendingClearIds, setPendingClearIds] = React.useState<number[]>([]);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([{ field: "triggeredAt", sort: "desc" }]);

  const searchKeyword = useSelector(searchKeywordSelector);
  const paginationModel = useSelector(paginationModelSelector);
  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector);
  const deviceFilterData = useSelector(devicesSelector);
  const selectedGroupKey = useSelector(selectedNavListItemSelector);
  const currentSitegroups = useSelector(currentSiteGroupsSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const { geoRegions } = useRegionDisplay();
  const tableId = isDeviceView ? "thresholdCrossingDevice" : "thresholdCrossing";
  const tablePagination = paginationModel[tableId] || initialState.pagination;

  // Use filter state from Redux for Region selection
  const regionFilterIds = React.useMemo(() => deviceFilterData.regionFilterIds ?? [], [deviceFilterData.regionFilterIds]);

  // Derive region options for the dropdown based on global selection
  const regionOptions = React.useMemo(() => {
    if (!geoRegions || !geoRegions.length) return [];
    if (selectedGeoRegionIds && selectedGeoRegionIds.length) {
      return geoRegions
        .filter((r: any) => selectedGeoRegionIds.includes(r.objectKey))
        .slice()
        .sort((a: any, b: any) => (a?.name || "").localeCompare(b?.name || ""));
    }
    return geoRegions
      .slice()
      .sort((a: any, b: any) => (a?.name || "").localeCompare(b?.name || ""));
  }, [geoRegions, selectedGeoRegionIds]);

  // Handler for region filter changes (matches GeneralFilter logic)
  const handleRegionChange = (event: any) => {
    const value = event.target.value;
    const selectedIds = typeof value === 'string' ? value.split(',') : value;
    dispatch(setRegionFilterIds(selectedIds));

    // Synchronize with nav panel and global region (same as GeneralFilter)
    const nextRegion = selectedIds.length === 1
      ? geoRegions.find((region: any) => region.objectKey === selectedIds[0])
      : undefined;

    const nextNavItem = selectedIds.length === 0 ? "" : "";

    dispatch(setSelectedNavListItems(nextNavItem));
    dispatch(setSelectedDeviceSiteGroup(""));
    dispatch(setSelectedGeoRegion(nextRegion || initialGeoRegion));
  };

  React.useEffect(() => {
    if (!isDeviceView) {
      return;
    }
    const deviceKpiType = (selectedDevice as { kpiType?: string } | null)?.kpiType;
    if (deviceKpiType) {
      const derived = deviceKpiType.toString().toUpperCase() === "NR" ? "NR" : "LTE";
      setNetworkType(derived);
      setSelectedKpi("");
      return;
    }
    if (!selectedDevice?.nodeType) {
      return;
    }
    const nt = selectedDevice.nodeType.toString().toUpperCase();
    const derived = ["SN", "BBU", "TBD"].includes(nt) ? "LTE" : "NR";
    setNetworkType(derived);
    setSelectedKpi("");
  }, [isDeviceView, selectedDevice]);

  React.useEffect(() => {
    if (isDeviceView) {
      return;
    }
    setSelectedKpi("");
  }, [isDeviceView, networkType]);

  const kpiOptions = React.useMemo(() => {
    const list = networkType === "LTE" ? LteKpiList : NrKpiList;
    return list.map((kpi) => ({ value: kpi.kpi, label: kpi.kpi }));
  }, [networkType]);

  const timeRangeWindow = React.useMemo(() => {
    if (!selectedTimeRange) {
      return null;
    }

    const to = new Date();
    const from = new Date(to);
    switch (selectedTimeRange) {
      case "30m":
        from.setMinutes(from.getMinutes() - 30);
        break;
      case "1h":
        from.setHours(from.getHours() - 1);
        break;
      case "24h":
        from.setHours(from.getHours() - 24);
        break;
      case "7d":
        from.setDate(from.getDate() - 7);
        break;
      default:
        return null;
    }

    return {
      from: from.toISOString(),
      to: to.toISOString(),
    };
  }, [selectedTimeRange]);

  const requestFilters = React.useMemo(() => {
    const params = new URLSearchParams();
    if (isDeviceView) {
      if (!effectiveDeviceId) {
        return "";
      }
      params.set("deviceId", effectiveDeviceId);
      if (selectedCell.trim()) {
        params.set("cellId", selectedCell.trim());
      }
      if (selectedKpi) {
        params.set("rulename", selectedKpi);
      }
      if (selectedState === "ACTIVE") {
        params.set("active", "true");
      } else if (selectedState === "CLEARED") {
        params.set("active", "false");
      }
      if (timeRangeWindow) {
        params.set("from", timeRangeWindow.from);
        params.set("to", timeRangeWindow.to);
      }
      params.set("page", tablePagination.page.toString());
      params.set("size", tablePagination.pageSize.toString());
      return params.toString();
    }


    // Build region names based on filter priority:
    // regionFilterIds > navRegionFilterIds > selectedGeoRegionIds
    let regionNames: string[] = [];
    if (geoRegions && geoRegions.length) {
      if (regionFilterIds && regionFilterIds.length > 0) {
        regionNames = geoRegions
          .filter((r: any) => regionFilterIds.includes(r.objectKey))
          .map((r: any) => r.name)
          .filter((name: string) => name && name.trim());
      } else if (selectedGeoRegionIds && selectedGeoRegionIds.length) {
        regionNames = geoRegions
          .filter((r: any) => selectedGeoRegionIds.includes(r.objectKey))
          .map((r: any) => r.name)
          .filter((name: string) => name && name.trim());
      }
    }

    if (regionNames.length > 0) {
      params.set("region", regionNames.join(","));
    }
    if (selectedCell.trim()) {
      params.set("cellId", selectedCell.trim());
    }
    if (selectedKpi) {
      params.set("rulename", selectedKpi);
    }
    if (networkType) {
      params.set("kpitype", networkType);
    }
    const groupKeys = ["offline", "disconnected", "pmCollected", "maintenancemode"];
    const isGroupKey = groupKeys.includes(selectedGroupKey);
    const groupValue = selectedGroupKey === "pmCollected" ? "false" : "true";
    const selectedSiteGroupObject = currentSitegroups?.find((group: any) => group.objectKey === selectedGroupKey);
    const selectedSiteGroupName = selectedSiteGroupObject?.name || "";
    if (isGroupKey) {
      params.set(selectedGroupKey, groupValue);
    } else if (selectedSiteGroupName) {
      params.set("siteGroup", selectedSiteGroupName);
    } else {
      params.set("allDevices", "true");
    }
    if (selectedState === "ACTIVE") {
      params.set("active", "true");
    } else if (selectedState === "CLEARED") {
      params.set("active", "false");
    }
    if (timeRangeWindow) {
      params.set("from", timeRangeWindow.from);
      params.set("to", timeRangeWindow.to);
    }
    params.set("page", tablePagination.page.toString());
    params.set("size", tablePagination.pageSize.toString());
    if (searchKeyword) {
      params.set("search", searchKeyword);
    }
    const activeSort = sortModel[0];
    if (activeSort?.field && activeSort.sort) {
      params.append("sort", `${activeSort.field},${activeSort.sort}`);
    }
    return params.toString();
  }, [
    currentSitegroups,
    geoRegions,
    isDeviceView,
    effectiveDeviceId,
    networkType,
    regionFilterIds,
    searchKeyword,
    selectedGeoRegionIds,
    selectedGroupKey,
    selectedCell,
    selectedKpi,
    selectedState,
    sortModel,
    timeRangeWindow,
    tablePagination.page,
    tablePagination.pageSize,
  ]);

  const handleSortModelChange = React.useCallback((model: GridSortModel) => {
    const nextSort = model[0];
    if (!nextSort?.field || !nextSort.sort) {
      setSortModel([{ field: "triggeredAt", sort: "desc" }]);
      return;
    }
    setSortModel([{ field: nextSort.field, sort: nextSort.sort }]);
  }, []);

  const {
    data: thresholdData,
    isLoading: isThresholdLoading,
    refetch: refetchThresholds,
  } = useGetThresholdAlertsQuery(requestFilters ? { filters: requestFilters } : skipToken, {
    pollingInterval: API.ENDPOINTS.THRESHOLDALERT.pollingInterval || API.POLLING_INTERVAL,
  });

  const rows = React.useMemo(() => {
    const raw = thresholdData as any;
    const dataList = raw?.data || raw?.content || raw?.data?.content || [];
    return Array.isArray(dataList) ? dataList : [];
  }, [thresholdData]);

  const rowCount = React.useMemo(() => {
    const raw = thresholdData as any;
    return raw?.totalItems || raw?.totalElements || rows.length;
  }, [rows.length, thresholdData]);

  const { clearSingle, clearBulk, isLoading: isClearing } = useClearKpiAlertThreshold();
  const [getDrawerCounts] = useGetDrawerCountsMutation();
  const [getDeviceFullDetail] = useGetDeviceFullDetailMutation();
  const [getAlarmsByDeviceKey] = useGetAlarmsByDeviceKeyMutation();
  const [exportThresholdAlerts, exportStatus] = useExportThresholdAlertsMutation();

  const handleRefetch = React.useCallback(() => {
    refetchThresholds?.();
  }, [refetchThresholds]);

  const requestClear = React.useCallback((ids: number[]) => {
    setPendingClearIds(ids);
    dispatch(setConfirmDialog(DIALOGOPENFOR.CLEARKPIALERTTHRESHOLD));
  }, [dispatch]);

  const requestClearSingle = React.useCallback((id: number) => {
    requestClear([id]);
  }, [requestClear]);

  const handleCellNavigation = React.useCallback(
    async (row: any) => {
      const deviceKey = row?.device?.objectKey || row?.deviceId;
      const cellFilter = (row?.cellName || row?.cellId || "").toString().trim();
      if (!deviceKey || !cellFilter) {
        return;
      }

      let deviceDetail = row?.device || null;
      try {
        const args = {
          filters: `key=objectKey&value=${deviceKey}&weightType=FULL`,
          deviceKey,
        };
        const response: any = await getDeviceFullDetail(args);
        if ("data" in response && response?.data?.data?.[0]) {
          deviceDetail = { ...response.data.data[0], ...response.data.deviceParams };
        }
      } catch {
        // fall back to row device payload
      }

      navigate(`/device/${deviceKey}/performance`, {
        state: {
          deviceDetail,
          initialPerformanceTab: "cell",
          initialCellSearch: cellFilter,
          navigationRequestId: Date.now(),
        },
      });
    },
    [getDeviceFullDetail, navigate]
  );

  const columns = React.useMemo(
    () => thresholdCrossingColumns({
      showDevice: !isDeviceView,
      onClearRequested: requestClearSingle,
      onCellRequested: handleCellNavigation,
      canClear: canManage,
    }),
    [canManage, handleCellNavigation, isDeviceView, requestClearSingle]
  );

  const handleBulkClear = async () => {
    const ids = rowSelectionModel.map((i) => Number(i)).filter((id) => Number.isFinite(id));
    if (!ids.length) {
      return;
    }
    requestClear(ids);
  };

  const handleConfirmClear = async (actionName: string) => {
    if (actionName !== "clear") {
      setPendingClearIds([]);
      return;
    }
    const ids = pendingClearIds;
    setPendingClearIds([]);
    const ok = ids.length === 1
      ? await clearSingle(ids[0])
      : await clearBulk(ids);
    if (ok) {
      setRowSelectionModel([]);
      handleRefetch();
    }
  };

  const handleReset = React.useCallback(() => {
    setSelectedCell("");
    setSelectedKpi("");
    setSelectedState("ALL");
    setSelectedTimeRange("");
    setRowSelectionModel([]);
    if (!isDeviceView) {
      setNetworkType("LTE");
      dispatch(resetKeyword());
      dispatch(setRegionFilterIds([]));
      dispatch(setSelectedNavListItems("devices"));
      dispatch(setSelectedDeviceSiteGroup(""));
      dispatch(setSelectedGeoRegion(initialGeoRegion));
    }
    handleRefetch();
  }, [dispatch, handleRefetch, isDeviceView]);

  const handleExport = async () => {
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (isDeviceView) {
        if (!effectiveDeviceId) {
          return;
        }
        const historyParams = new URLSearchParams();
        historyParams.set("deviceId", effectiveDeviceId);
        if (selectedState === "ACTIVE") {
          historyParams.set("active", "true");
        } else if (selectedState === "CLEARED") {
          historyParams.set("active", "false");
        }
        if (selectedKpi) {
          historyParams.set("rulename", selectedKpi);
        }
        if (selectedCell.trim()) {
          historyParams.set("cellId", selectedCell.trim());
        }
        if (timeRangeWindow) {
          historyParams.set("from", timeRangeWindow.from);
          historyParams.set("to", timeRangeWindow.to);
        }
        if (browserTimezone) {
          historyParams.set("tz", browserTimezone);
        }
        const response = await exportThresholdAlerts({ filters: historyParams.toString() }).unwrap();
        exportInCSV({ data: response }, "ThresholdCrossing");
        return;
      }
      const exportParams = new URLSearchParams(requestFilters);
      if (browserTimezone) {
        exportParams.set("tz", browserTimezone);
      }
      const response = await exportThresholdAlerts({ filters: exportParams.toString() }).unwrap();
      exportInCSV({ data: response }, "ThresholdCrossing");
    } catch (error: any) {
      dispatch(setOpenToast({
        openToast: true,
        toastType: "error",
        message: errorMessage(error?.message ?? error?.data?.message ?? error),
      }));
    }
  };

  const isExportLoading = exportStatus.isLoading;

  const stateFilters = React.useMemo(() => [
    { value: "ALL", label: "All" },
    { value: "ACTIVE", label: "Active" },
    { value: "CLEARED", label: "Cleared" },
  ], []);

  const networkFilters = React.useMemo(() => [
    { value: "LTE", label: "LTE" },
    { value: "NR", label: "NR" },
  ], []);

  const timeRangeFilters = React.useMemo(() => [
    { value: "", label: "All Time" },
    { value: "30m", label: "Last 30 mins" },
    { value: "1h", label: "Last hour" },
    { value: "24h", label: "Last 24 hours" },
    { value: "7d", label: "Last 7 days" },
  ], []);

  const regionSelectOptions = React.useMemo(() => {
    return (regionOptions || []).map((opt: any) => ({ label: opt.name || "", value: opt.objectKey }));
  }, [regionOptions]);

  const thresholdFilters = React.useMemo(() => {
    const filters: any[] = [
      {
        id: "cellFilter",
        label: "Cell",
        value: selectedCell,
        setValue: ((_: any) => { setSelectedCell(""); return { type: "NOOP" }; }) as any,
        values: [],
      },
      {
        id: "kpiFilter",
        label: "KPI",
        value: selectedKpi || "",
        setValue: ((_: any) => { setSelectedKpi(""); return { type: "NOOP" }; }) as any,
        values: kpiOptions.map((o) => ({ text: o.label, value: o.value })),
      },
      {
        id: "stateFilter",
        label: "State",
        value: selectedState,
        setValue: ((_: any) => { setSelectedState("ALL"); return { type: "NOOP" }; }) as any,
        values: stateFilters.filter((f) => f.value !== "ALL").map((f) => ({ text: f.label, value: f.value })),
      },
      {
        id: "timeRangeFilter",
        label: "Time Range",
        value: selectedTimeRange,
        setValue: ((_: any) => { setSelectedTimeRange(""); return { type: "NOOP" }; }) as any,
        values: timeRangeFilters.filter((f) => f.value !== "").map((f) => ({ text: f.label, value: f.value })),
      },

    ];
    if (!isDeviceView) {
      filters.push(
        {
          id: "networkFilter",
          label: "Network",
          value: networkType,
          setValue: ((_: any) => { setNetworkType("LTE"); return { type: "NOOP" }; }) as any,
          values: networkFilters.map((f) => ({ text: f.label, value: f.value })),
        },
        {
          id: "regionFilter",
          label: "Region",
          value: regionFilterIds && regionFilterIds.length > 0 ? regionFilterIds : [],
          setValue: setRegionFilterIds as any,
          values: regionSelectOptions.map((o: any) => ({ text: o.label, value: o.value })),
          multiple: true,
        }
      );
    }
    return filters;
  }, [selectedCell, selectedKpi, selectedState, networkType, regionFilterIds, regionSelectOptions, kpiOptions, isDeviceView, selectedTimeRange, networkFilters, stateFilters, timeRangeFilters]);

  const handleClearSearch = React.useCallback(() => {
    dispatch(resetKeyword());
  }, [dispatch]);

  const handleClearAllFilters = React.useCallback(() => {
    handleReset();
  }, [handleReset]);

  const handleRowClicked: GridEventListener<"rowClick"> = React.useCallback(
    async (params: GridRowParams<any>) => {
      const device = params?.row?.device;
      if (!device || device.modelName === "migration" || device.modelName === "unknown") {
        return;
      }
      const objectKey = device.objectKey || "";
      if (!objectKey) {
        return;
      }
      setRowClicked(true);
      dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: true, isLoadingAlarms: true }));
      dispatch(setOpenDeviceDrawer(true));
      try {
        const args = { filters: `key=objectKey&value=${objectKey}&weightType=FULL`, deviceKey: objectKey };
        const apiPromises: any[] = [];
        apiPromises.push(
          getDrawerCounts({
            deviceKey: objectKey,
            filters: "",
          })
        );
        apiPromises.push(getDeviceFullDetail(args));
        if (permissions?.hasOpCodeGroup("view_events")) {
          apiPromises.push(
            getAlarmsByDeviceKey({
              deviceKey: objectKey,
              filters: "?sort=retention%2Cdesc&page=0&size=50",
            })
          );
        }

        const responses = await Promise.all(apiPromises);
        responses?.forEach((response) => {
          if ("error" in response && response?.error) {
            throw response?.error;
          }
        });

        let configCount: any;
        let deviceData: any;
        let alarmData: any;
        responses?.forEach((response, index) => {
          if ("data" in response && index === 0) {
            configCount = response.data;
          } else if ("data" in response && index === 1) {
            deviceData = response.data;
          } else if ("data" in response && index === 2) {
            alarmData = response.data;
          }
        });

        if (configCount && deviceData?.data?.[0]) {
          dispatch(setDrawerCounts(configCount));
          dispatch(setSelectedDevice({ ...deviceData.data[0], ...deviceData?.deviceParams }));
        }
        if (permissions?.hasOpCodeGroup("view_events") && alarmData) {
          dispatch(setSelectedDeviceAlarms(alarmData?.data));
          dispatch(setSelectedDeviceAlarmCount(alarmData?.totalItems || 0));
        }
        dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
      } catch (error: any) {
        dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
        dispatch(setOpenDeviceDrawer(false));
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    },
    [dispatch, getAlarmsByDeviceKey, getDeviceFullDetail, getDrawerCounts, permissions]
  );

  return (
    <>
      {!isDeviceView && rowClicked && (
        <DeviceDrawer data={selectedDevice} closedButtonClicked={() => setRowClicked(false)} />
      )}
      {openConfirmDialog === DIALOGOPENFOR.CLEARKPIALERTTHRESHOLD && (
        <ConfirmDialog actionName="Clear" handleClickAction={handleConfirmClear}>
          {pendingClearIds.length === 1
            ? "Do you want to clear this alert?"
            : "Do you want to clear these alerts?"}
        </ConfirmDialog>
      )}
      <div
        className="flex flex-col w-full"
        style={{ color: "var(--foreground)" }}
      >
        <div className="bg-card text-card-foreground shadow">
          <div className="flex flex-col">
            <div className="pt-4 px-1">
              {/* Filters row */}
              <div className="flex items-center flex-wrap gap-3 mb-3">
                {canManage && rowSelectionModel.length > 0 && (
                  <Button size="sm" onClick={handleBulkClear} disabled={isClearing}>
                    {isClearing ? "Clearing..." : "Clear"}
                  </Button>
                )}

                {!isDeviceView && <SearchBox tooltip="" />}

                {!isDeviceView && (
                  <RadixFilterMultiSelect
                    options={regionSelectOptions}
                    value={regionFilterIds || []}
                    onValueChange={(selectedIds) => {
                      handleRegionChange({ target: { value: selectedIds } } as any);
                    }}
                    placeholder="Region"
                    width="200px"
                  />
                )}

                <div className="w-[200px] shrink-0">
                  <Input
                    value={selectedCell}
                    onChange={(event) => setSelectedCell(event.target.value)}
                    placeholder="Cell"
                    className="h-8"
                  />
                </div>

                <div className="w-[200px] shrink-0">
                  <RadixSelectControl
                    value={selectedKpi || "__placeholder__"}
                    onValueChange={(val) => setSelectedKpi(val === "__placeholder__" ? "" : val)}
                    options={[
                      { value: "__placeholder__", label: "KPI" },
                      ...kpiOptions,
                    ]}
                    placeholder="KPI"
                  />
                </div>

                {!isDeviceView && (
                  <div className="inline-flex rounded-md border border-border overflow-hidden h-8">
                    {networkFilters.map((filter) => (
                      <button
                        key={filter.value}
                        onClick={() => setNetworkType(filter.value as "LTE" | "NR")}
                        className={`px-3 text-sm font-medium border-r last:border-r-0 border-border transition-colors ${networkType === filter.value
                          ? "bg-primary text-primary-foreground"
                          : "bg-transparent text-muted-foreground hover:bg-accent"
                          }`}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                )}

                <div className="inline-flex rounded-md border border-border overflow-hidden h-8">
                  {stateFilters.map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => setSelectedState(filter.value)}
                      className={`px-3 text-sm font-medium border-r last:border-r-0 border-border transition-colors ${selectedState === filter.value
                        ? "bg-primary text-primary-foreground"
                        : "bg-transparent text-muted-foreground hover:bg-accent"
                        }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>

                <div className="w-[170px] shrink-0">
                  <RadixSelectControl
                    value={selectedTimeRange || "__placeholder__"}
                    onValueChange={(val) => setSelectedTimeRange(val === "__placeholder__" ? "" : val)}
                    options={[
                      { value: "__placeholder__", label: "All Time" },
                      ...timeRangeFilters.filter(f => f.value !== "").map(f => ({ value: f.value, label: f.label })),
                    ]}
                    placeholder="Time Range"
                  />
                </div>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-6 w-8 shrink-0"
                        onClick={handleReset}
                        aria-label="Reset All"
                      >
                        <FilterX className="h-4 w-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Reset All</TooltipContent>
                </Tooltip>
              </div>

              {/* Results count + Active filter chips */}
              <div className="flex justify-between items-end mb-0 mt-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground">{`Showing ${rows?.length || 0} results`}</span>
                  <ActiveFilterChips
                    filters={thresholdFilters}
                    searchKeyword={isDeviceView ? undefined : searchKeyword}
                    onClearSearch={isDeviceView ? undefined : handleClearSearch}
                    onClearAll={handleClearAllFilters}
                    dispatch={dispatch}
                  />
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-6 w-8 shrink-0 relative -top-1"
                  onClick={handleExport}
                  disabled={isExportLoading || rows.length === 0}
                  aria-label="Export CSV"
                >
                  {isExportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon name="download" size={18} />}
                </Button>
              </div>
              <CustomDataGrid
                tableId={tableId}
                rows={isThresholdLoading ? [] : rows}
                columns={columns}
                rowCount={rowCount}
                loading={isThresholdLoading}
                checkboxSelection={true}
                disableColumnMenu={true}
                getRowId={(row: any) => row.id}
                paginationMode={isDeviceView ? "client" : "server"}
                sortingMode={isDeviceView ? "client" : "server"}
                initialSortModel={isDeviceView ? undefined : sortModel}
                onSortModelChange={isDeviceView ? undefined : handleSortModelChange}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(newModel: any) => setRowSelectionModel(newModel as any[])}
                disableRowSelectionOnClick={true}
                isRowSelectable={(params) => !params.row?.cleared}
                onRowClick={isDeviceView ? undefined : handleRowClicked}
                fillContainer
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ThresholdCrossing;
